(() => {
  // Build-time copy: each PDP owns its lifecycle; no cross-product runtime dependency.
  function createDisclosureMotion({ signal, motion, openClass, onChange = () => {} }) {
    const running = new Map();
    const wrappers = new Map();
    const pending = new Map();
    const paintStyles = new Map();
    let frame = null;
    const naturalHeight = panel => {
      const style = getComputedStyle(panel);
      return panel.getBoundingClientRect().height + (parseFloat(style.marginTop) || 0) + (parseFloat(style.marginBottom) || 0);
    };
    function settle(panel, open) {
      const record = running.get(panel);
      running.delete(panel);
      record?.animation?.cancel();
      panel.removeAttribute('data-pdp-disclosure-animating');
      panel.hidden = !open;
      panel.inert = !open;
      panel.setAttribute('aria-hidden', String(!open));
      if (openClass) panel.classList.toggle(openClass, open);
      const wrapper = wrappers.get(panel);
      if (wrapper) {
        wrapper.querySelector('[data-pdp-disclosure-clip]').style.height = 'auto';
        wrapper.style.height = open ? 'auto' : '0px';
        wrapper.style.display = open ? 'flow-root' : 'none';
      }
    }
    function flush() {
      frame = null;
      // Read all starting frames before changing either side of an accordion group.
      const changes = Array.from(pending, ([panel, open]) => ({
        panel, open, previous: running.get(panel),
        start: running.has(panel) ? running.get(panel).wrapper.getBoundingClientRect().height : panel.hidden ? 0 : naturalHeight(panel)
      }));
      pending.clear();
      changes.forEach(change => {
        const { panel, previous, start } = change;
        const wrapper = wrappers.get(panel) || document.createElement('div');
        change.wrapper = wrapper;
        if (!wrappers.has(panel)) {
          wrapper.dataset.pdpDisclosureMotion = '';
          wrapper.style.cssText = 'position:relative;display:flow-root;flex-shrink:0;align-self:stretch;width:100%;min-width:0;overflow:visible;overflow-anchor:none;contain:layout style;';
          const surface = document.createElement('div');
          surface.dataset.pdpDisclosureSurface = '';
          surface.setAttribute('aria-hidden', 'true');
          surface.style.cssText = 'display:block;position:absolute;left:0;right:0;pointer-events:none;';
          const clip = document.createElement('div');
          clip.dataset.pdpDisclosureClip = '';
          clip.style.cssText = 'position:relative;display:flow-root;overflow:clip;';
          wrapper.append(surface, clip);
          paintStyles.set(panel, ['background', 'box-shadow'].map(property =>
            [property, panel.style.getPropertyValue(property), panel.style.getPropertyPriority(property)]));
          panel.before(wrapper);
          wrapper.querySelector('[data-pdp-disclosure-clip]').append(panel);
          wrappers.set(panel, wrapper);
        }
        wrapper.style.display = 'flow-root';
        wrapper.style.height = `${start}px`;
        // Read the current responsive paint from the actual panel before transferring it.
        paintStyles.get(panel).forEach(([property, value, priority]) => {
          if (value) panel.style.setProperty(property, value, priority);
          else panel.style.removeProperty(property);
        });
        previous?.animation?.cancel();
        panel.hidden = false;
        panel.style.removeProperty('display');
        if (openClass) panel.classList.add(openClass);
      });
      // Padding and text stay at their natural size inside a single clipped area.
      changes.forEach(change => {
        change.fullHeight = naturalHeight(change.panel);
        change.end = change.open ? change.fullHeight : 0;
        const style = getComputedStyle(change.panel);
        change.edgeGap = parseFloat(style.paddingBottom) || 16;
        change.paint = { background: style.background, boxShadow: style.boxShadow,
          borderRadius: style.borderRadius, top: style.marginTop, bottom: style.marginBottom };
      });
      changes.forEach(({ panel, open, wrapper, start, end, fullHeight, edgeGap, paint }) => {
        Object.assign(wrapper.querySelector("[data-pdp-disclosure-surface]").style, paint);
        panel.style.setProperty("background", "transparent");
        panel.style.setProperty("box-shadow", "none");
        const record = { wrapper, open, animation: null };
        running.set(panel, record);
        if (motion.matches || typeof wrapper.animate !== 'function' || Math.abs(start - end) < 0.5) {
          settle(panel, open);
          return;
        }
        // A real empty strip travels with the rounded surface. Its shadow remains
        // outside the content clip throughout opening, closing and reversals.
        wrapper.querySelector('[data-pdp-disclosure-clip]').style.height = `max(0px, calc(100% - ${edgeGap}px))`;
        record.animation = wrapper.animate([{ height: `${start}px` }, { height: `${end}px` }], {
          // Compact benefits take at least 320ms; longer answers keep their existing pace.
          // Preserve the accepted lower FAQ timing and proportional interrupted reversals.
          duration: panel.matches('.productcard__item__additionally__answer')
            ? Math.max(64, Math.round(Math.max(320, Math.round(fullHeight * 346 / 224 * 0.7)) * Math.min(1, Math.abs(end - start) / Math.max(1, fullHeight))))
            : Math.round(Math.max(64, Math.round(Math.abs(end - start) * 346 / 224)) * 0.7),
          easing: 'cubic-bezier(0.25, 0.1, 0.25, 1)', fill: 'both'
        });
        record.animation.onfinish = () => {
          if (running.get(panel) !== record) return;
          settle(panel, record.open);
          onChange();
        };
      });
      onChange();
    }
    function set(panel, open, animate = true) {
      if (!panel || signal.aborted) return;
      panel.inert = !open;
      panel.setAttribute('aria-hidden', String(!open));
      if (!animate || motion.matches || typeof panel.animate !== 'function') {
        pending.delete(panel);
        settle(panel, open);
        onChange();
        return;
      }
      if (!running.has(panel) && !pending.has(panel) && panel.hidden === !open) return;
      panel.setAttribute('data-pdp-disclosure-animating', '');
      pending.set(panel, open);
      if (frame === null) frame = requestAnimationFrame(flush);
      onChange();
    }
    function finishAll() {
      if (frame !== null) cancelAnimationFrame(frame);
      frame = null;
      const finalStates = new Map(Array.from(running, ([panel, record]) => [panel, record.open]));
      pending.forEach((open, panel) => finalStates.set(panel, open));
      pending.clear();
      finalStates.forEach((open, panel) => settle(panel, open));
      onChange();
    }
    motion.addEventListener?.('change', finishAll, { signal });
    signal.addEventListener('abort', () => {
      finishAll();
      wrappers.forEach((wrapper, panel) => {
        paintStyles.get(panel).forEach(([property, value, priority]) => {
          if (value) panel.style.setProperty(property, value, priority);
          else panel.style.removeProperty(property);
        });
        wrapper.replaceWith(panel);
      });
      wrappers.clear(); paintStyles.clear();
    }, { once: true });
    return { set, finishAll };
  }

  // Prepare hidden FAQ media before its trigger enters the viewport.
  function prepareDisclosureMedia(root, { signal, motion }) {
    const itemSelector = '.productcard__item__faq__box, .productcard__item__additionally__item';
    const prepared = new WeakSet();
    const preparedVideos = new WeakSet();
    function prepare(item) {
      if (!item || signal.aborted) return;
      if (!prepared.has(item)) item.querySelectorAll('img').forEach(image => {
        image.decoding = 'async';
        image.loading = 'eager';
        if (image.dataset.srcset) { image.srcset = image.dataset.srcset; image.removeAttribute('data-srcset'); }
        if (image.dataset.src) { image.src = image.dataset.src; image.removeAttribute('data-src'); }
        image.decode?.().catch(() => {});
      });
      prepared.add(item);
      if (motion.matches) return;
      item.querySelectorAll('video').forEach(video => {
        if (preparedVideos.has(video)) return;
        preparedVideos.add(video);
        video.preload = 'auto';
        let hydrated = false;
        [video, ...video.querySelectorAll('source[data-src]')].forEach(source => {
          if (!source.dataset.src) return;
          source.src = source.dataset.src;
          source.removeAttribute('data-src');
          hydrated = true;
        });
        if (hydrated || video.networkState === 0) video.load();
      });
    }
    const observer = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        prepare(entry.target);
      });
    }, { rootMargin: '800px 0px', threshold: 0 }) : null;
    root.querySelectorAll(itemSelector).forEach(item => {
      if (observer) observer.observe(item);
      else prepare(item);
    });
    motion.addEventListener?.('change', () => {
      if (motion.matches) return;
      root.querySelectorAll(itemSelector).forEach(item => {
        if (observer) observer.observe(item);
        else prepare(item);
      });
    }, { signal });
    // Keyboard navigation may jump directly into a still-offscreen item.
    root.addEventListener('focusin', event => prepare(event.target.closest(itemSelector)), { signal });
    const items = Array.from(root.querySelectorAll(itemSelector));
    let warmIndex = 0;
    let idleHandle = null;
    let timeoutHandle = null;
    const warmNext = deadline => {
      if (signal.aborted) return;
      let count = 0;
      const batchSize = deadline ? 2 : 1;
      while (warmIndex < items.length && count < batchSize && (!deadline || deadline.didTimeout || deadline.timeRemaining() > 4)) {
        prepare(items[warmIndex++]);
        count += 1;
      }
      if (warmIndex >= items.length) return;
      if ('requestIdleCallback' in window) idleHandle = window.requestIdleCallback(warmNext, { timeout: 2000 });
      else timeoutHandle = setTimeout(() => warmNext(null), 400);
    };
    const warmAll = () => {
      if (signal.aborted || !items.length) return;
      if ('requestIdleCallback' in window) idleHandle = window.requestIdleCallback(warmNext, { timeout: 2000 });
      else timeoutHandle = setTimeout(() => warmNext(null), 1000);
    };
    if (document.readyState === 'complete') warmAll();
    else window.addEventListener('load', warmAll, { once: true, signal });
    signal.addEventListener('abort', () => {
      observer?.disconnect();
      if (idleHandle !== null && 'cancelIdleCallback' in window) window.cancelIdleCallback(idleHandle);
      if (timeoutHandle !== null) clearTimeout(timeoutHandle);
    }, { once: true });
  }

    const elementName = 'ai-pbn-product-card-controller';
    if (customElements.get(elementName)) return;

    class AiPbnProductCardController extends HTMLElement {
      connectedCallback() {
        if (this.dataset.aiPbnProductCardInitialized === 'true') return;
        const sectionRoot = this.closest('.shopify-section');
        const productInfo = this.querySelector('[data-ai-pbn-product-card-root]');
        const sectionId = this.dataset.sectionId;
        if (!sectionRoot || !productInfo || !sectionId) return;

        this.dataset.aiPbnProductCardInitialized = 'true';
        sectionRoot.dataset.aiPbnProductCardInitialized = 'true';
        this.__aiPbnProductCardSectionRoot = sectionRoot;
        const controller = new AbortController();
        const signal = controller.signal;

        const shippingThresholds = productInfo.querySelectorAll('[data-ai-pbn-shipping-threshold]');
        if (shippingThresholds.length) {
            const firstThreshold = shippingThresholds[0];
            const baseCents = Number(firstThreshold.dataset.baseCents);
            const currencyRate = Number(window.Shopify?.currency?.rate);
            const currencyCode = window.Shopify?.currency?.active || firstThreshold.dataset.currencyCode || 'USD';
            const locale = (firstThreshold.dataset.locale || document.documentElement.lang || 'en').replace('_', '-');

            if (Number.isFinite(baseCents) && baseCents > 0 && Number.isFinite(currencyRate) && currencyRate > 0) {
                const convertedAmount = (baseCents * currencyRate) / 100;
                const roundingStep = convertedAmount < 100 ? 5 : convertedAmount < 1000 ? 10 : 100;
                const roundedAmount = Math.ceil(convertedAmount / roundingStep) * roundingStep;
                let formattedAmount;

                try {
                    formattedAmount = new Intl.NumberFormat(locale, {
                        style: 'currency',
                        currency: currencyCode,
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0
                    }).format(roundedAmount);
                } catch (error) {
                    formattedAmount = `${currencyCode} ${roundedAmount}`;
                }

                shippingThresholds.forEach(element => {
                    element.textContent = formattedAmount;
                });
            }
        }

        const mobileMedia = window.matchMedia('(max-width: 600px)');
        const imageModal = document.getElementById('ProductModal-' + sectionId);
        imageModal?.classList.add('ai-pbn-product-media-modal');
        const viewerItems = imageModal
            ? Array.from(imageModal.querySelectorAll('.product-media-modal__content > [data-media-id]'))
            : [];
        let viewerIndex = 0;
        let viewerPrevious = null;
        let viewerNext = null;
        let viewerMediaIndex = -1;
        let viewerGesture = null;
        let viewerScrollTargetIndex = null;
        let viewerScrollFrame = null;
        let viewerScrollTimer = null;
        let viewerTransitionTimer = null;
        let viewerPositionFrame = null;
        let viewerHorizontalFrame = null;
        const viewerMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        sectionRoot.__destroyAiPbnProductCard = () => {
            try {
                if (imageModal?.hasAttribute('open')) imageModal.hide?.();
            } finally {
                controller.abort();
                clearViewerTransition();
                if (viewerScrollFrame !== null) window.cancelAnimationFrame(viewerScrollFrame);
                if (viewerPositionFrame !== null) window.cancelAnimationFrame(viewerPositionFrame);
                if (viewerHorizontalFrame !== null) window.cancelAnimationFrame(viewerHorizontalFrame);
                if (viewerScrollTimer !== null) window.clearTimeout(viewerScrollTimer);
                imageModal?.remove();
                delete sectionRoot.dataset.aiPbnProductCardInitialized;
                delete this.dataset.aiPbnProductCardInitialized;
                delete this.__aiPbnProductCardSectionRoot;
                delete sectionRoot.__destroyAiPbnProductCard;
            }
        };

        const mainBuyButton = productInfo.querySelector('.productcard__item-buy');
        const stickyBuyButton = productInfo.querySelector('.totalCardBar__item-buy');
        const mainProductForm = mainBuyButton?.closest('form');
        let selectedVariantAvailable = Boolean(mainBuyButton && !mainBuyButton.disabled);
        let stickySubmitRequested = false;

        const isMainAddPending = () => Boolean(
            mainBuyButton?.classList.contains('loading') ||
            mainBuyButton?.getAttribute('aria-busy') === 'true' ||
            mainProductForm?.dataset.cartSubmitPending === 'true'
        );

        const syncStickyBuyButton = () => {
            if (!stickyBuyButton) return;
            const pending = stickySubmitRequested || isMainAddPending();

            stickyBuyButton.classList.toggle('loading', pending);
            stickyBuyButton.disabled = pending || !selectedVariantAvailable;

            if (pending) {
                stickyBuyButton.setAttribute('aria-busy', 'true');
                stickyBuyButton.setAttribute('aria-disabled', 'true');
            } else {
                stickyBuyButton.removeAttribute('aria-busy');
                if (selectedVariantAvailable) {
                    stickyBuyButton.removeAttribute('aria-disabled');
                } else {
                    stickyBuyButton.setAttribute('aria-disabled', 'true');
                }
            }
        };

        const buyButtonObserver = mainBuyButton
            ? new MutationObserver(syncStickyBuyButton)
            : null;
        buyButtonObserver?.observe(mainBuyButton, {
            attributes: true,
            attributeFilter: ['aria-busy', 'class', 'disabled']
        });
        signal.addEventListener('abort', () => buyButtonObserver?.disconnect(), { once: true });

        stickyBuyButton?.addEventListener('click', () => {
            if (stickyBuyButton.disabled || !mainBuyButton || mainBuyButton.disabled || isMainAddPending()) return;

            stickySubmitRequested = true;
            syncStickyBuyButton();
            mainBuyButton.click();

            queueMicrotask(() => {
                stickySubmitRequested = false;
                syncStickyBuyButton();
            });
        }, { signal });

        syncStickyBuyButton();

        const variantSelect = productInfo.querySelector('[data-ai-pbn-variant-select]');
        variantSelect?.addEventListener('change', () => {
            const option = variantSelect.selectedOptions[0];
            if (!option) return;

            const available = option.dataset.available === 'true';
            const money = option.dataset.price || '';
            const moneyWithCurrency = option.dataset.priceCurrency || money;
            const onSale = option.dataset.onSale === 'true';
            selectedVariantAvailable = available;

            const mainPrice = productInfo.querySelector('#dynamic-pricetot-' + CSS.escape(sectionId));
            if (mainPrice) mainPrice.textContent = moneyWithCurrency;

            const comparePrice = productInfo.querySelector('[data-ai-pbn-compare-price]');
            const comparePriceValue = comparePrice?.querySelector('[data-ai-pbn-compare-price-value]');
            if (comparePrice) comparePrice.hidden = !onSale;
            if (comparePriceValue) comparePriceValue.textContent = option.dataset.comparePrice || '';

            [mainBuyButton, stickyBuyButton].filter(Boolean).forEach(button => {
                button.disabled = !available;
                const label = button.querySelector('[data-ai-pbn-add-label]');
                const separator = button.querySelector('[data-ai-pbn-add-separator]');
                const price = button.querySelector('[id^="dynamic-price-"]');
                if (label) label.textContent = available ? productInfo.dataset.addToCartLabel : productInfo.dataset.soldOutLabel;
                if (separator) separator.hidden = !available;
                if (price) {
                    price.textContent = money;
                    price.hidden = !available;
                }
            });

            syncStickyBuyButton();

            const stickyImage = productInfo.querySelector('[data-ai-pbn-sticky-image]');
            if (stickyImage && option.dataset.featuredImage) {
                stickyImage.src = option.dataset.featuredImage;
                stickyImage.alt = option.dataset.featuredAlt || stickyImage.alt;
            }

            if (option.dataset.mediaId) {
                productInfo.dispatchEvent(new CustomEvent('crochet:variant-change', {
                    detail: { mediaId: option.dataset.mediaId }
                }));
            }

            if (option.dataset.url && window.history?.replaceState) {
                const currentUrl = new URL(window.location.href);
                const nextUrl = new URL(option.dataset.url, currentUrl.origin);
                const nextVariant = nextUrl.searchParams.get('variant') || option.value;
                currentUrl.pathname = nextUrl.pathname;
                if (nextVariant) currentUrl.searchParams.set('variant', nextVariant);
                window.history.replaceState(
                    window.history.state,
                    '',
                    currentUrl.pathname + currentUrl.search + currentUrl.hash
                );
            }
        }, { signal });

        function clearViewerTransition() {
            if (viewerTransitionTimer !== null) {
                window.clearTimeout(viewerTransitionTimer);
                viewerTransitionTimer = null;
            }
        }

        function clearViewerPositioning() {
            if (viewerPositionFrame !== null) {
                window.cancelAnimationFrame(viewerPositionFrame);
                viewerPositionFrame = null;
            }
            imageModal?.classList.remove('ai-pbn-media-viewer--positioning');
        }

        function cancelViewerHorizontalSettle() {
            if (viewerHorizontalFrame !== null) {
                window.cancelAnimationFrame(viewerHorizontalFrame);
                viewerHorizontalFrame = null;
            }
            imageModal?.classList.remove('ai-pbn-media-viewer--horizontal');
        }

        function updateViewerControls() {
            // Move focus before disabling the arrow; keep Escape in the dialog.
            const activeControl = document.activeElement;
            if ((viewerIndex <= 0 && activeControl === viewerPrevious)
                || (viewerIndex >= viewerItems.length - 1 && activeControl === viewerNext)) {
                imageModal.querySelector('[id^="ModalClose-"]')?.focus({ preventScroll: true });
            }
            if (viewerPrevious) viewerPrevious.disabled = viewerIndex <= 0;
            if (viewerNext) viewerNext.disabled = viewerIndex >= viewerItems.length - 1;
        }

        function pauseViewerMedia() {
            viewerItems.forEach(item => {
                item.querySelectorAll('video').forEach(video => video.pause());
            });
        }

        function syncViewerMedia() {
            pauseViewerMedia();
            const currentItem = viewerItems[viewerIndex];
            if (!currentItem) return;
            viewerMediaIndex = viewerIndex;

            const deferredMedia = currentItem.matches('deferred-media')
                ? currentItem
                : currentItem.querySelector('deferred-media');
            if (deferredMedia && typeof deferredMedia.loadContent === 'function') {
                deferredMedia.loadContent(false);
            }

            const video = currentItem.matches('video') ? currentItem : currentItem.querySelector('video');
            if (!(video instanceof HTMLVideoElement)) return;

            video.controls = false;
            video.removeAttribute('controls');
            video.loop = true;
            video.muted = true;
            video.defaultMuted = true;
            video.autoplay = true;
            video.playsInline = true;
            video.setAttribute('playsinline', '');
            const playPromise = video.play();
            playPromise?.catch?.(() => {});
        }

        function setViewerIndex(index) {
            if (!viewerItems.length) return;
            const nextIndex = Math.max(0, Math.min(viewerItems.length - 1, index));
            const indexChanged = nextIndex !== viewerIndex;
            viewerIndex = nextIndex;
            viewerItems.forEach((item, itemIndex) => {
                const isCurrent = itemIndex === viewerIndex;

                item.classList.toggle('active', isCurrent);
                item.hidden = false;
                item.setAttribute('aria-hidden', String(!isCurrent));
                if (isCurrent) {
                    item.removeAttribute('inert');
                } else {
                    item.setAttribute('inert', '');
                }
            });
            if (indexChanged || viewerMediaIndex !== viewerIndex) syncViewerMedia();
            updateViewerControls();
        }

        function resetViewerMotion() {
            clearViewerTransition();
            clearViewerPositioning();
            cancelViewerHorizontalSettle();
            imageModal?.classList.remove(
                'ai-pbn-media-viewer--dragging',
                'ai-pbn-media-viewer--closing'
            );
            imageModal?.style.removeProperty('--ai-pbn-viewer-drag-y');
            imageModal?.style.removeProperty('--ai-pbn-viewer-surface-opacity');
            imageModal?.style.removeProperty('--ai-pbn-viewer-media-scale');
            imageModal?.style.removeProperty('--ai-pbn-viewer-controls-opacity');
            imageModal?.style.removeProperty('--ai-pbn-viewer-transition-duration');
        }

        function updateViewerDragMotion(deltaY) {
            if (!imageModal) return;
            const viewportHeight = Math.max(1, window.innerHeight);
            const progress = Math.min(1, Math.abs(deltaY) / Math.min(420, viewportHeight * 0.5));
            const surfaceOpacity = Math.max(0.18, 1 - (progress * 0.82));
            const mediaScale = Math.max(0.965, 1 - (progress * 0.035));
            const controlsOpacity = Math.max(0, 1 - (progress * 1.2));

            imageModal.style.setProperty('--ai-pbn-viewer-drag-y', `${deltaY}px`);
            imageModal.style.setProperty('--ai-pbn-viewer-surface-opacity', surfaceOpacity.toFixed(3));
            imageModal.style.setProperty('--ai-pbn-viewer-media-scale', mediaScale.toFixed(3));
            imageModal.style.setProperty('--ai-pbn-viewer-controls-opacity', controlsOpacity.toFixed(3));
        }

        function settleViewerGesture() {
            if (!imageModal) return;
            clearViewerTransition();
            imageModal.classList.remove('ai-pbn-media-viewer--dragging');
            imageModal.style.setProperty('--ai-pbn-viewer-transition-duration', '220ms');
            imageModal.style.setProperty('--ai-pbn-viewer-drag-y', '0px');
            imageModal.style.setProperty('--ai-pbn-viewer-surface-opacity', '1');
            imageModal.style.setProperty('--ai-pbn-viewer-media-scale', '1');
            imageModal.style.setProperty('--ai-pbn-viewer-controls-opacity', '1');
            viewerTransitionTimer = window.setTimeout(() => {
                viewerTransitionTimer = null;
                resetViewerMotion();
            }, 240);
        }

        function viewerScrollLeftFor(index) {
            const content = imageModal?.querySelector('.product-media-modal__content');
            const item = viewerItems[index];
            if (!content || !item) return 0;
            return item.offsetLeft - ((content.clientWidth - item.offsetWidth) / 2);
        }

        function scrollViewerToIndex(index, behavior = 'smooth') {
            const content = imageModal?.querySelector('.product-media-modal__content');
            if (!content || !viewerItems.length) return;
            const nextIndex = Math.max(0, Math.min(viewerItems.length - 1, index));
            viewerScrollTargetIndex = nextIndex;
            setViewerIndex(nextIndex);
            content.scrollTo({
                left: viewerScrollLeftFor(nextIndex),
                behavior: viewerMotion.matches ? 'auto' : behavior
            });
        }

        function navigateViewer(delta) {
            if (!imageModal || !delta) return;
            scrollViewerToIndex(viewerIndex + delta);
        }

        function nearestViewerIndexForScrollLeft(content, scrollLeft) {
            const viewportCenter = scrollLeft + (content.clientWidth / 2);
            return viewerItems.reduce((nearestIndex, item, itemIndex) => {
                const itemCenter = item.offsetLeft + (item.offsetWidth / 2);
                const nearestItem = viewerItems[nearestIndex];
                const nearestCenter = nearestItem.offsetLeft + (nearestItem.offsetWidth / 2);
                return Math.abs(itemCenter - viewportCenter) < Math.abs(nearestCenter - viewportCenter)
                    ? itemIndex
                    : nearestIndex;
            }, 0);
        }

        function nearestViewerIndex(content) {
            return nearestViewerIndexForScrollLeft(content, content.scrollLeft);
        }

        function settleViewerHorizontal(content, gesture, cancelled = false) {
            if (!content || !gesture) return;
            cancelViewerHorizontalSettle();
            imageModal?.classList.add('ai-pbn-media-viewer--horizontal');

            const maxScrollLeft = Math.max(0, content.scrollWidth - content.clientWidth);
            const currentScrollLeft = Math.max(0, Math.min(maxScrollLeft, content.scrollLeft));
            const projectedScrollLeft = currentScrollLeft - (gesture.velocityX * 260);
            let targetIndex = nearestViewerIndexForScrollLeft(content, projectedScrollLeft);
            const weakRelease = Math.abs(gesture.deltaX) < 28 && Math.abs(gesture.velocityX) < 0.32;
            if (cancelled || weakRelease) targetIndex = gesture.startIndex;
            targetIndex = Math.max(
                gesture.startIndex - 1,
                Math.min(gesture.startIndex + 1, targetIndex)
            );
            targetIndex = Math.max(0, Math.min(viewerItems.length - 1, targetIndex));

            const targetScrollLeft = Math.max(
                0,
                Math.min(maxScrollLeft, viewerScrollLeftFor(targetIndex))
            );
            const travel = targetScrollLeft - currentScrollLeft;
            const finish = () => {
                viewerHorizontalFrame = null;
                content.scrollLeft = targetScrollLeft;
                imageModal?.classList.remove('ai-pbn-media-viewer--horizontal');
                viewerScrollTargetIndex = null;
                setViewerIndex(targetIndex);
            };

            if (viewerMotion.matches || Math.abs(travel) <= 1) {
                finish();
                return;
            }

            const duration = Math.round(Math.max(
                280,
                Math.min(420, 330 + (Math.abs(travel) * 0.18) - (Math.min(1.8, Math.abs(gesture.velocityX)) * 35))
            ));
            const startedAt = window.performance.now();
            const animate = now => {
                const progress = Math.min(1, (now - startedAt) / duration);
                const eased = 1 - Math.pow(1 - progress, 3);
                content.scrollLeft = currentScrollLeft + (travel * eased);
                if (progress < 1) {
                    viewerHorizontalFrame = window.requestAnimationFrame(animate);
                    return;
                }
                finish();
            };
            viewerHorizontalFrame = window.requestAnimationFrame(animate);
        }

        function closeViewerFromGesture(direction = -1, releaseVelocity = 0) {
            if (!imageModal) return;
            if (viewerMotion.matches) {
                pauseViewerMedia();
                window.MyArtModalMotion?.cancel(imageModal);
                imageModal.finishHide();
                resetViewerMotion();
                return;
            }

            clearViewerTransition();
            imageModal.classList.remove('ai-pbn-media-viewer--dragging');
            imageModal.classList.add('ai-pbn-media-viewer--closing');
            const currentOffset = Math.abs(
                Number.parseFloat(imageModal.style.getPropertyValue('--ai-pbn-viewer-drag-y')) || 0
            );
            const exitDistance = window.innerHeight + 48;
            const remainingRatio = Math.max(0.7, Math.min(1, (exitDistance - currentOffset) / exitDistance));
            const duration = Math.round(Math.max(
                210,
                Math.min(300, (300 - (Math.min(1.8, releaseVelocity) * 45)) * remainingRatio)
            ));
            imageModal.style.setProperty('--ai-pbn-viewer-transition-duration', `${duration}ms`);
            imageModal.style.setProperty(
                '--ai-pbn-viewer-drag-y',
                `${Math.sign(direction || -1) * exitDistance}px`
            );
            imageModal.style.setProperty('--ai-pbn-viewer-surface-opacity', '0');
            imageModal.style.setProperty('--ai-pbn-viewer-media-scale', '0.965');
            imageModal.style.setProperty('--ai-pbn-viewer-controls-opacity', '0');
            viewerTransitionTimer = window.setTimeout(() => {
                viewerTransitionTimer = null;
                pauseViewerMedia();
                // The swipe already completed the exit: finish before resetting
                // the photograph, without starting a second modal animation.
                window.MyArtModalMotion?.cancel(imageModal);
                imageModal.finishHide();
                resetViewerMotion();
            }, duration + 20);
        }

        function setupSourceMatchedViewer() {
            if (!imageModal || !viewerItems.length) return;
            const dialog = imageModal.querySelector('.product-media-modal__dialog');
            const content = imageModal.querySelector('.product-media-modal__content');
            const closeButton = imageModal.querySelector('.product-media-modal__toggle');
            if (!dialog || !content || !closeButton) return;

            closeButton.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6.5 6.5 17.5 17.5M17.5 6.5 6.5 17.5"></path></svg>';

            let controls = dialog.querySelector('.ai-pbn-media-viewer__controls');
            if (!controls) {
                controls = document.createElement('div');
                controls.className = 'ai-pbn-media-viewer__controls';
                viewerPrevious = document.createElement('button');
                viewerPrevious.type = 'button';
                viewerPrevious.className = 'ai-pbn-media-viewer__nav ai-pbn-media-viewer__nav--previous';
                viewerPrevious.setAttribute('aria-label', 'Previous image');
                viewerPrevious.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m15 5-7 7 7 7"></path></svg>';

                viewerNext = document.createElement('button');
                viewerNext.type = 'button';
                viewerNext.className = 'ai-pbn-media-viewer__nav ai-pbn-media-viewer__nav--next';
                viewerNext.setAttribute('aria-label', 'Next image');
                viewerNext.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7"></path></svg>';

                closeButton.classList.add('ai-pbn-media-viewer__close');
                controls.append(viewerPrevious, closeButton, viewerNext);
                dialog.append(controls);
            } else {
                viewerPrevious = controls.querySelector('.ai-pbn-media-viewer__nav--previous');
                viewerNext = controls.querySelector('.ai-pbn-media-viewer__nav--next');
            }

            controls.addEventListener('pointerup', event => event.stopPropagation(), { signal });
            viewerPrevious?.addEventListener('click', () => navigateViewer(-1), { signal });
            viewerNext?.addEventListener('click', () => navigateViewer(1), { signal });
            imageModal.addEventListener('keydown', event => {
                if (!imageModal.hasAttribute('open')) return;
                if (event.key === 'ArrowLeft') {
                    event.preventDefault();
                    navigateViewer(-1);
                } else if (event.key === 'ArrowRight') {
                    event.preventDefault();
                    navigateViewer(1);
                }
            }, { signal });
            document.body.addEventListener('modalClosed', () => {
                if (!imageModal.hasAttribute('open')) {
                    viewerGesture = null;
                    viewerScrollTargetIndex = null;
                    pauseViewerMedia();
                    viewerMediaIndex = -1;
                    resetViewerMotion();
                }
            }, { signal });

            content.addEventListener('scroll', () => {
                if (viewerScrollFrame !== null) return;
                viewerScrollFrame = window.requestAnimationFrame(() => {
                    viewerScrollFrame = null;
                    if (imageModal.classList.contains('ai-pbn-media-viewer--horizontal')) return;
                    const nearestIndex = nearestViewerIndex(content);
                    if (viewerScrollTargetIndex === null) {
                        setViewerIndex(nearestIndex);
                    } else if (Math.abs(content.scrollLeft - viewerScrollLeftFor(viewerScrollTargetIndex)) <= 2) {
                        viewerScrollTargetIndex = null;
                        setViewerIndex(nearestIndex);
                    }
                });

                if (viewerScrollTimer !== null) window.clearTimeout(viewerScrollTimer);
                viewerScrollTimer = window.setTimeout(() => {
                    viewerScrollTimer = null;
                    if (imageModal.classList.contains('ai-pbn-media-viewer--horizontal')) return;
                    const nearestIndex = nearestViewerIndex(content);
                    viewerScrollTargetIndex = null;
                    setViewerIndex(nearestIndex);
                    if (
                        !mobileMedia.matches
                        && Math.abs(content.scrollLeft - viewerScrollLeftFor(nearestIndex)) > 2
                    ) {
                        content.scrollTo({
                            left: viewerScrollLeftFor(nearestIndex),
                            behavior: viewerMotion.matches ? 'auto' : 'smooth'
                        });
                    }
                }, 120);
            }, { passive: true, signal });

            content.addEventListener('pointerdown', event => {
                if (!mobileMedia.matches || viewerTransitionTimer !== null) return;
                if (!event.isPrimary) {
                    const interruptedGesture = viewerGesture;
                    viewerGesture = null;
                    if (interruptedGesture?.axis === 'horizontal') {
                        const interruptedIndex = nearestViewerIndex(content);
                        content.scrollLeft = viewerScrollLeftFor(interruptedIndex);
                        setViewerIndex(interruptedIndex);
                    }
                    resetViewerMotion();
                    return;
                }
                if (event.pointerType === 'mouse' && event.button !== 0) return;
                if (event.target.closest('button, a, iframe, model-viewer, [controls]')) return;

                cancelViewerHorizontalSettle();

                viewerGesture = {
                    pointerId: event.pointerId,
                    startX: event.clientX,
                    startY: event.clientY,
                    startScrollLeft: content.scrollLeft,
                    startIndex: nearestViewerIndex(content),
                    lastX: event.clientX,
                    lastY: event.clientY,
                    lastTime: event.timeStamp,
                    deltaX: 0,
                    deltaY: 0,
                    velocityX: 0,
                    velocityY: 0,
                    axis: null
                };
            }, { signal });

            content.addEventListener('pointermove', event => {
                if (!viewerGesture || event.pointerId !== viewerGesture.pointerId) return;
                const deltaX = event.clientX - viewerGesture.startX;
                const deltaY = event.clientY - viewerGesture.startY;
                if (viewerGesture.axis === null) {
                    const absoluteX = Math.abs(deltaX);
                    const absoluteY = Math.abs(deltaY);
                    const distance = Math.hypot(deltaX, deltaY);
                    if (distance < 6) return;

                    const verticalIntent = absoluteY >= 6 && absoluteY >= absoluteX * 1.15;
                    const horizontalIntent = absoluteX >= 6 && absoluteX >= absoluteY * 1.15;
                    if (!verticalIntent && !horizontalIntent && distance < 10) return;

                    viewerGesture.axis = verticalIntent
                        ? 'vertical'
                        : horizontalIntent
                            ? 'horizontal'
                            : absoluteY >= absoluteX * 0.85
                                ? 'vertical'
                                : 'horizontal';
                    try {
                        content.setPointerCapture?.(event.pointerId);
                    } catch (error) {
                        // Pointer capture is an enhancement; the gesture still works without it.
                    }
                    if (viewerGesture.axis === 'horizontal') {
                        viewerScrollTargetIndex = null;
                        imageModal.classList.add('ai-pbn-media-viewer--horizontal');
                    }
                }

                const elapsed = Math.max(1, event.timeStamp - viewerGesture.lastTime);
                const instantVelocityX = (event.clientX - viewerGesture.lastX) / elapsed;
                const instantVelocity = (event.clientY - viewerGesture.lastY) / elapsed;
                viewerGesture.velocityX = (viewerGesture.velocityX * 0.55) + (instantVelocityX * 0.45);
                viewerGesture.velocityY = (viewerGesture.velocityY * 0.55) + (instantVelocity * 0.45);
                viewerGesture.lastX = event.clientX;
                viewerGesture.lastY = event.clientY;
                viewerGesture.lastTime = event.timeStamp;
                viewerGesture.deltaX = deltaX;
                viewerGesture.deltaY = deltaY;
                event.preventDefault();

                if (viewerGesture.axis === 'horizontal') {
                    content.scrollLeft = viewerGesture.startScrollLeft - deltaX;
                    return;
                }

                imageModal.classList.add('ai-pbn-media-viewer--dragging');
                updateViewerDragMotion(deltaY);
            }, { passive: false, signal });

            const finishViewerGesture = (event, cancelled = false) => {
                if (!viewerGesture || event.pointerId !== viewerGesture.pointerId) return;
                const completedGesture = viewerGesture;
                viewerGesture = null;
                if (completedGesture.axis === null) return;
                event.stopPropagation();
                try {
                    content.releasePointerCapture?.(event.pointerId);
                } catch (error) {
                    // The browser may already have released capture on cancellation.
                }

                if (completedGesture.axis === 'horizontal') {
                    settleViewerHorizontal(content, completedGesture, cancelled);
                    return;
                }

                if (cancelled) {
                    settleViewerGesture();
                    return;
                }

                const distanceThreshold = Math.min(144, Math.max(104, window.innerHeight * 0.16));
                const sameDirection = Math.sign(completedGesture.deltaY) === Math.sign(completedGesture.velocityY);
                const directionalVelocity = sameDirection ? completedGesture.velocityY : 0;
                const projectedDeltaY = completedGesture.deltaY + (directionalVelocity * 190);
                const directionConsistent = sameDirection || Math.abs(completedGesture.velocityY) < 0.25;
                const enoughDistance = Math.abs(completedGesture.deltaY) >= distanceThreshold;
                const enoughProjectedDistance = Math.abs(completedGesture.deltaY) >= 96
                    && Math.abs(directionalVelocity) >= 0.45
                    && Math.abs(projectedDeltaY) >= distanceThreshold;
                if (directionConsistent && (enoughDistance || enoughProjectedDistance)) {
                    const direction = completedGesture.deltaY || directionalVelocity;
                    closeViewerFromGesture(direction, Math.abs(directionalVelocity));
                    return;
                }
                settleViewerGesture();
            };

            content.addEventListener('pointerup', finishViewerGesture, { signal });
            content.addEventListener('pointercancel', event => finishViewerGesture(event, true), { signal });
            updateViewerControls();
        }

        setupSourceMatchedViewer();

        function openImage(trigger) {
            if (!imageModal || !trigger || typeof imageModal.show !== 'function') return;
            const mediaId = trigger.getAttribute('data-media-id');
            const targetIndex = viewerItems.findIndex(item => item.getAttribute('data-media-id') === mediaId);
            const nextIndex = targetIndex >= 0 ? targetIndex : 0;
            const content = imageModal.querySelector('.product-media-modal__content');
            resetViewerMotion();
            imageModal.classList.add('ai-pbn-media-viewer--positioning');
            imageModal.show(trigger);
            setViewerIndex(nextIndex);
            if (content) content.scrollLeft = viewerScrollLeftFor(nextIndex);
            viewerScrollTargetIndex = null;
            viewerPositionFrame = window.requestAnimationFrame(() => {
                viewerPositionFrame = window.requestAnimationFrame(() => {
                    viewerPositionFrame = null;
                    imageModal.classList.remove('ai-pbn-media-viewer--positioning');
                });
            });
            imageModal.querySelector('.product-media-modal__toggle')?.focus({ preventScroll: true });
        }

        productInfo.querySelectorAll('.productcard__media').forEach(media => {
            const slider = media.querySelector('.productcard__item__zoom__slider');
            if (!slider) return;
            const galleryMobileMedia = media.closest('.ai-pbn-paint-product-card-section')
                ? window.matchMedia('(max-width: 989px)')
                : mobileMedia;
            const slides = Array.from(slider.children);
            const thumbs = Array.from(media.children).filter(element => element.classList.contains('productcard__item-image'));
            let activeIndex = 0;
            let gesture = null;
            let scrollFrame = null;
            let slideStep = 0;
            let suppressClick = false;
            let suppressClickTimer = null;
            const galleryMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
            const desktopHostedVideos = thumbs
                .map(thumb => thumb.querySelector('video'))
                .filter(video => video instanceof HTMLVideoElement);
            const desktopVideoVisibility = new Map(desktopHostedVideos.map(video => [video, 0]));
            const desktopVideoObserver = typeof IntersectionObserver === 'function'
                ? new IntersectionObserver(entries => {
                    entries.forEach(entry => {
                        desktopVideoVisibility.set(entry.target, entry.isIntersecting ? entry.intersectionRatio : 0);
                    });
                    syncDesktopHostedVideos();
                }, { threshold: [0, 0.55, 1] })
                : null;

            function pauseDesktopHostedVideos() {
                desktopHostedVideos.forEach(video => video.pause());
            }

            function syncDesktopHostedVideos() {
                const canAutoplay = !galleryMobileMedia.matches
                    && !galleryMotion.matches
                    && document.visibilityState === 'visible'
                    && !imageModal?.hasAttribute('open');

                desktopHostedVideos.forEach(video => {
                    const shouldPlay = canAutoplay && (desktopVideoVisibility.get(video) || 0) >= 0.55;
                    if (!shouldPlay) {
                        video.pause();
                        return;
                    }

                    video.muted = true;
                    video.defaultMuted = true;
                    video.playsInline = true;
                    const playPromise = video.play();
                    playPromise?.catch?.(() => {});
                });
            }

            desktopHostedVideos.forEach(video => desktopVideoObserver?.observe(video));

            function clampIndex(index) {
                return Math.max(0, Math.min(slides.length - 1, index));
            }

            function getSlideStep() {
                if (slideStep > 0) return slideStep;
                if (slides.length > 1) {
                    const measuredStep = slides[1].offsetLeft - slides[0].offsetLeft;
                    if (measuredStep > 0) {
                        slideStep = measuredStep;
                        return slideStep;
                    }
                }
                slideStep = slider.clientWidth;
                return slideStep;
            }

            function updateSelection(index) {
                activeIndex = clampIndex(index);
                const isMobile = galleryMobileMedia.matches;
                slides.forEach((slide, slideIndex) => {
                    const isActive = slideIndex === activeIndex;
                    slide.hidden = false;
                    slide.classList.toggle('active', isActive);
                    if (isMobile) {
                        slide.setAttribute('aria-hidden', String(!isActive));
                        if (slide.hasAttribute('data-ai-pbn-product-media-trigger')) {
                            slide.tabIndex = isActive ? 0 : -1;
                        }
                    } else {
                        slide.removeAttribute('aria-hidden');
                        if (slide.hasAttribute('data-ai-pbn-product-media-trigger')) slide.tabIndex = 0;
                    }

                    const hostedVideo = slide.matches('video') ? slide : slide.querySelector('video');
                    if (hostedVideo) {
                        if (isMobile && isActive && !galleryMotion.matches) {
                            const playPromise = hostedVideo.play();
                            playPromise?.catch?.(() => {});
                        } else {
                            hostedVideo.pause();
                        }
                    }
                });
                thumbs.forEach((thumb, thumbIndex) => {
                    const isActive = thumbIndex === activeIndex;
                    thumb.classList.toggle('active', isActive);
                    thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
                    const mediaLabel = thumb.dataset.mediaLabel;
                    if (mediaLabel) {
                        thumb.setAttribute('aria-label', isMobile
                            ? `Show ${mediaLabel} in product gallery`
                            : `Open ${mediaLabel} in image viewer`);
                    }
                });
            }

            function alignToIndex(index, behavior = 'auto') {
                if (!galleryMobileMedia.matches) return;
                slider.scrollTo({
                    left: clampIndex(index) * getSlideStep(),
                    behavior: galleryMotion.matches ? 'auto' : behavior
                });
            }

            function renderGallery() {
                slider.classList.remove('is-mouse-dragging');
                if (!galleryMobileMedia.matches) {
                    slider.scrollLeft = 0;
                } else {
                    window.requestAnimationFrame(() => alignToIndex(activeIndex));
                }
                updateSelection(activeIndex);
            }

            function syncSelectionFromScroll() {
                scrollFrame = null;
                if (!galleryMobileMedia.matches) return;
                const step = getSlideStep();
                if (step <= 0) return;
                const nextIndex = clampIndex(Math.round(slider.scrollLeft / step));
                if (nextIndex !== activeIndex) updateSelection(nextIndex);
            }

            function queueScrollSync() {
                if (scrollFrame !== null) return;
                scrollFrame = window.requestAnimationFrame(syncSelectionFromScroll);
            }

            function selectSlide(index, behavior = 'smooth') {
                updateSelection(index);
                alignToIndex(activeIndex, behavior);
            }

            function armClickSuppression() {
                suppressClick = true;
                if (suppressClickTimer !== null) window.clearTimeout(suppressClickTimer);
                suppressClickTimer = window.setTimeout(() => {
                    suppressClick = false;
                    suppressClickTimer = null;
                }, 350);
            }

            function finishGesture(event) {
                if (!gesture || (event && event.pointerId !== gesture.pointerId)) return;
                const currentGesture = gesture;
                gesture = null;

                slider.classList.remove('is-mouse-dragging');
                if (slider.hasPointerCapture?.(currentGesture.pointerId)) {
                    slider.releasePointerCapture(currentGesture.pointerId);
                }

                if (currentGesture.dragged) {
                    armClickSuppression();
                    if (currentGesture.pointerType === 'mouse') {
                        selectSlide(Math.round(slider.scrollLeft / getSlideStep()));
                    }
                }
            }

            slider.addEventListener('scroll', queueScrollSync, { passive: true, signal });

            slides.forEach(slide => {
                if (!slide.hasAttribute('data-ai-pbn-product-media-trigger')) return;
                if (slide instanceof HTMLImageElement) {
                    slide.addEventListener('dragstart', event => {
                        if (galleryMobileMedia.matches) event.preventDefault();
                    }, { signal });
                }
                slide.addEventListener('click', event => {
                    if (suppressClick) {
                        event.preventDefault();
                        event.stopPropagation();
                        return;
                    }
                    openImage(slide);
                }, { signal });
                slide.addEventListener('keydown', event => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        openImage(slide);
                    }
                }, { signal });
            });

            thumbs.forEach((thumb, index) => {
                thumb.addEventListener('click', event => {
                    if (galleryMobileMedia.matches) {
                        event.preventDefault();
                        selectSlide(index);
                    } else if (thumb.hasAttribute('data-ai-pbn-product-media-trigger')) {
                        openImage(thumb);
                    }
                }, { signal });
                thumb.addEventListener('keydown', event => {
                    if (event.key !== 'Enter' && event.key !== ' ') return;
                    event.preventDefault();
                    if (galleryMobileMedia.matches) {
                        selectSlide(index);
                    } else if (thumb.hasAttribute('data-ai-pbn-product-media-trigger')) {
                        openImage(thumb);
                    }
                }, { signal });
            });

            productInfo.addEventListener('crochet:variant-change', event => {
                const mediaId = String(event.detail?.mediaId || '');
                const targetIndex = thumbs.findIndex(thumb => thumb.getAttribute('data-media-id') === mediaId);
                if (targetIndex >= 0) selectSlide(targetIndex);
            }, { signal });

            // Consume only the click belonging to the completed drag.
            slider.addEventListener('click', event => {
                if (!suppressClick) return;
                suppressClick = false;
                if (suppressClickTimer !== null) window.clearTimeout(suppressClickTimer);
                suppressClickTimer = null;
                event.preventDefault();
                event.stopPropagation();
            }, { capture: true, signal });

            slider.addEventListener('pointerdown', event => {
                // A new intentional activation never inherits the preceding drag's suppression.
                suppressClick = false;
                if (suppressClickTimer !== null) window.clearTimeout(suppressClickTimer);
                suppressClickTimer = null;
                if (!galleryMobileMedia.matches || slides.length < 2 || !event.isPrimary) return;
                if (event.pointerType === 'mouse' && event.button !== 0) return;
                gesture = {
                    pointerId: event.pointerId,
                    pointerType: event.pointerType,
                    startX: event.clientX,
                    startY: event.clientY,
                    startScrollLeft: slider.scrollLeft,
                    horizontal: null,
                    dragged: false
                };
            }, { signal });
            slider.addEventListener('pointermove', event => {
                if (!gesture || event.pointerId !== gesture.pointerId || !galleryMobileMedia.matches) return;
                const deltaX = event.clientX - gesture.startX;
                const deltaY = event.clientY - gesture.startY;

                if (gesture.horizontal === null && (Math.abs(deltaX) >= 6 || Math.abs(deltaY) >= 6)) {
                    gesture.horizontal = Math.abs(deltaX) > Math.abs(deltaY);
                }
                if (gesture.horizontal !== true) return;

                gesture.dragged = Math.abs(deltaX) >= 8;
                if (gesture.dragged) suppressClick = true;

                if (gesture.pointerType === 'mouse') {
                    // Capture only a real drag; eager capture retargets image clicks to the slider.
                    if (gesture.dragged && !slider.hasPointerCapture?.(event.pointerId)) {
                        slider.setPointerCapture?.(event.pointerId);
                    }
                    event.preventDefault();
                    slider.classList.add('is-mouse-dragging');
                    slider.scrollLeft = gesture.startScrollLeft - deltaX;
                }
            }, { signal });
            slider.addEventListener('pointerup', finishGesture, { signal });
            slider.addEventListener('pointercancel', finishGesture, { signal });

            const galleryResizeObserver = typeof ResizeObserver === 'function'
                ? new ResizeObserver(() => {
                    slideStep = 0;
                    if (galleryMobileMedia.matches && !gesture) alignToIndex(activeIndex);
                })
                : null;
            galleryResizeObserver?.observe(slider);

            galleryMobileMedia.addEventListener?.('change', () => {
                gesture = null;
                slideStep = 0;
                slider.classList.remove('is-mouse-dragging');
                renderGallery();
                syncDesktopHostedVideos();
            }, { signal });
            galleryMotion.addEventListener?.('change', syncDesktopHostedVideos, { signal });
            document.addEventListener('visibilitychange', syncDesktopHostedVideos, { signal });
            document.body.addEventListener('modalClosed', syncDesktopHostedVideos, { signal });
            signal.addEventListener('abort', () => {
                if (scrollFrame !== null) window.cancelAnimationFrame(scrollFrame);
                if (suppressClickTimer !== null) window.clearTimeout(suppressClickTimer);
                slides.forEach(slide => slide.querySelector('video')?.pause());
                pauseDesktopHostedVideos();
                desktopVideoObserver?.disconnect();
                galleryResizeObserver?.disconnect();
            }, { once: true });
            renderGallery();
            syncDesktopHostedVideos();
        });

        const faqMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        prepareDisclosureMedia(productInfo, { signal, motion: faqMotion });
        const faqStepVideos = Array.from(productInfo.querySelectorAll('.ai-pbn-faq__step-video'));
        const faqStepVideoVisibility = new Map(faqStepVideos.map(video => [video, 0]));
        const faqStepVideosPlayed = new WeakSet();
        const faqStepVideoObserver = typeof IntersectionObserver === 'function'
            ? new IntersectionObserver(entries => {
                entries.forEach(entry => {
                    faqStepVideoVisibility.set(entry.target, entry.isIntersecting ? entry.intersectionRatio : 0);
                });
                syncFaqStepVideos();
            }, { rootMargin: '80px 0px', threshold: [0, 0.55, 1] })
            : null;

        function syncFaqStepVideos() {
            const canPlay = !faqMotion.matches && document.visibilityState === 'visible';

            faqStepVideos.forEach(video => {
                const panel = video.closest('.ai-pbn-faq__panel--process');
                const panelIsOpen = Boolean(panel && !panel.hidden && panel.getAttribute('aria-hidden') !== 'true');
                const isVisible = (faqStepVideoVisibility.get(video) || 0) >= 0.55;
                const shouldPlay = canPlay && panelIsOpen && isVisible && !panel.hasAttribute('data-pdp-disclosure-animating');

                if (!shouldPlay) {
                    video.pause();
                    faqStepVideosPlayed.delete(video);
                    return;
                }
                if (faqStepVideosPlayed.has(video)) return;

                video.muted = true;
                video.defaultMuted = true;
                video.loop = true;
                video.playsInline = true;
                video.currentTime = 0;
                faqStepVideosPlayed.add(video);
                const playPromise = video.play();
                playPromise?.catch?.(() => faqStepVideosPlayed.delete(video));
            });
        }

        faqStepVideos.forEach(video => {
            faqStepVideoObserver?.observe(video);
            video.addEventListener('loadeddata', syncFaqStepVideos, { signal });
        });
        faqMotion.addEventListener?.('change', syncFaqStepVideos, { signal });
        document.addEventListener('visibilitychange', syncFaqStepVideos, { signal });
        signal.addEventListener('abort', () => {
            faqStepVideoObserver?.disconnect();
            faqStepVideos.forEach(video => video.pause());
        }, { once: true });

        const faqDisclosure = createDisclosureMotion({ signal, motion: faqMotion, openClass: 'is-open', onChange: syncFaqStepVideos });
        function openFaqPanel(panel) { faqDisclosure.set(panel, true); }
        function closeFaqPanel(panel) { faqDisclosure.set(panel, false); }

        const accordionGroupSelector = '.productcard__item__faq, .productcard__item__additionally';
        const accordionItemSelector = '.productcard__item__faq__box, .productcard__item__additionally__item';
        const accordionTriggerSelector = '.productcard__item__faq__box-title, .productcard__item__additionally__box';
        const accordionPanelSelector = '.productcard__item__faq__section, .productcard__item__additionally__answer';

        function setOpenFaq(trigger) {
            const faq = trigger?.closest(accordionGroupSelector);
            const box = trigger?.closest(accordionItemSelector);
            const panel = box?.querySelector(accordionPanelSelector);
            if (!faq || !panel) return;

            faq.querySelectorAll(accordionTriggerSelector).forEach(otherTrigger => {
                const isActive = otherTrigger === trigger && Boolean(panel);
                otherTrigger.classList.toggle('active', isActive);
                otherTrigger.setAttribute('aria-expanded', isActive ? 'true' : 'false');
            });
            faq.querySelectorAll(accordionPanelSelector).forEach(otherPanel => {
                const isActive = otherPanel === panel;
                if (isActive) openFaqPanel(otherPanel);
                else closeFaqPanel(otherPanel);
            });
        }

        function setClosedFaq(trigger) {
            const box = trigger?.closest(accordionItemSelector);
            const panel = box?.querySelector(accordionPanelSelector);
            if (!trigger || !panel) return;

            trigger.classList.remove('active');
            trigger.setAttribute('aria-expanded', 'false');
            closeFaqPanel(panel);
        }

        productInfo.querySelectorAll(accordionItemSelector).forEach(faqBox => {
            const trigger = faqBox.querySelector(accordionTriggerSelector);
            const panel = faqBox.querySelector(accordionPanelSelector);
            if (!trigger || !panel) return;
            panel.style.removeProperty('display');
            const isOpen = trigger.getAttribute('aria-expanded') === 'true';
            panel.classList.toggle('is-open', isOpen);
            panel.hidden = !isOpen;
            panel.inert = !isOpen;
            panel.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
        });

        productInfo.querySelectorAll(accordionTriggerSelector).forEach(trigger => {
            trigger.style.setProperty('-webkit-tap-highlight-color', 'transparent');
            trigger.addEventListener('click', () => {
                const shouldOpen = trigger.getAttribute('aria-expanded') !== 'true';
                if (shouldOpen) setOpenFaq(trigger);
                else setClosedFaq(trigger);
            }, { signal });
        });

        document.addEventListener('shopify:block:select', event => {
            const selectedBox = event.target?.closest?.('.productcard__item__faq__box');
            if (!selectedBox || !productInfo.contains(selectedBox)) return;
            setOpenFaq(selectedBox.querySelector('.productcard__item__faq__box-title'));
        }, { signal });

        const rating = productInfo.querySelector('.productcard__item__rating');
        function scrollToReviews() {
            document.querySelector('.productreview, #looxReviews, [data-loox-component], .loox-reviews-default')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        rating?.addEventListener('click', scrollToReviews, { signal });

        function updateStickyBar() {
            productInfo.querySelector('.totalCardBar')?.classList.toggle('scroll', window.innerWidth <= 800 && window.scrollY > 0);
        }
        window.addEventListener('scroll', updateStickyBar, { passive: true, signal });
        window.addEventListener('resize', updateStickyBar, { signal });
        updateStickyBar();

      }

      disconnectedCallback() {
        this.__aiPbnProductCardSectionRoot?.__destroyAiPbnProductCard?.();
      }
    }

    customElements.define(elementName, AiPbnProductCardController);
})();
